import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Flag, CheckCircle2, XCircle, Loader2, ExternalLink, Server } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Specpixel staff review of cross-server network watch reports.
   Server staff file these from the in-game admin console; approving one puts the
   player on the network watchlist that every server's admin console annotates
   its live roster with (hubadmin._network_watchlist). */

const STATUS_CLS = {
  pending: "text-amber-400 border-amber-500/40",
  approved: "text-emerald-400 border-emerald-500/40",
  rejected: "text-red-400 border-red-500/40",
};

export default function AdminNetworkReports() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ reports: [], counts: {} });
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/network-reports", { params: { status: tab } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const review = async (id, decision) => {
    setBusy(id + decision);
    try {
      const r = await api.post(`/admin/network-reports/${id}/review`, { decision, note: notes[id] || "" });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-netrev" className="flex flex-col gap-4 max-w-4xl">
      <div className="border border-white/10 bg-[#0a0a0a] p-4">
        <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase flex items-center gap-2">
          <Flag className="w-3.5 h-3.5 text-nc-orange" /> Specpixel network watch — review queue
        </p>
        <p className="font-mono text-[10px] text-zinc-500 mt-2 leading-relaxed">
          Server staff flag problem players from their in-game admin console. Approving a report puts the player on the
          <span className="text-zinc-300"> network watchlist</span> — every New Citizen server's admin console then marks them in the live roster with their flag categories.
        </p>
      </div>

      <div className="flex gap-1 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button key={s} data-testid={`netrev-tab-${s}`} onClick={() => setTab(s)}
            className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${tab === s ? "bg-nc-orange text-black border-nc-orange" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
            {s}{s !== "all" && data.counts?.[s] != null ? ` (${data.counts[s]})` : ""}
          </button>
        ))}
      </div>

      {loading && <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-nc-orange" /></div>}
      {!loading && data.reports.length === 0 && (
        <p data-testid="netrev-empty" className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.25em] py-10 text-center border border-white/10">No {tab} reports</p>
      )}

      {data.reports.map((r) => (
        <div key={r.id} data-testid={`netrev-card-${r.id.slice(0, 8)}`} className="border border-white/10 bg-[#0a0a0a] p-5 flex flex-col gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="font-bold text-sm text-zinc-100">{r.target_name || "—"}</p>
            <span className="font-mono text-[10px] text-zinc-500">#{r.target_playercode}</span>
            {r.target_steamid && <span className="font-mono text-[9px] text-zinc-500">steam {r.target_steamid}</span>}
            <span className="font-mono text-[8px] uppercase border border-nc-orange/40 text-nc-orange px-1.5 py-0.5">{r.category}</span>
            <span className={`ml-auto font-mono text-[8px] uppercase border px-1.5 py-0.5 ${STATUS_CLS[r.status] || STATUS_CLS.pending}`}>{r.status}</span>
          </div>
          <p className="font-mono text-[11px] text-zinc-300 leading-relaxed">{r.reason}</p>
          {(r.evidence_url || r.evidence_text) && (
            <div className="border border-white/10 bg-black/40 px-3.5 py-2.5 flex flex-col gap-1">
              <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500">Evidence</p>
              {r.evidence_url && (
                <a data-testid={`netrev-evidence-${r.id.slice(0, 8)}`} href={r.evidence_url} target="_blank" rel="noopener noreferrer"
                  className="font-mono text-[10px] text-nc-blue hover:underline flex items-center gap-1.5 break-all">
                  <ExternalLink className="w-3 h-3 shrink-0" /> {r.evidence_url}
                </a>
              )}
              {r.evidence_text && <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">{r.evidence_text}</p>}
            </div>
          )}
          <p className="font-mono text-[9px] text-zinc-600 flex items-center gap-2 flex-wrap">
            <Server className="w-3 h-3" /> {r.server_name || r.server_id} · filed by pc:{r.reporter_pc} · {String(r.created_at).slice(0, 16).replace("T", " ")}
          </p>
          {r.status === "pending" ? (
            <div className="flex items-center gap-2 flex-wrap border-t border-white/5 pt-3">
              <input data-testid={`netrev-note-${r.id.slice(0, 8)}`} value={notes[r.id] || ""} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })} maxLength={500}
                placeholder="Review note (optional — shown back to the filing server)"
                className="flex-1 min-w-52 bg-[#050505] border border-white/10 px-3 py-2 font-mono text-[11px] text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-orange" />
              <button data-testid={`netrev-approve-${r.id.slice(0, 8)}`} disabled={!!busy} onClick={() => review(r.id, "approved")}
                className="px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] bg-emerald-500 text-black flex items-center gap-1.5 disabled:opacity-40">
                {busy === r.id + "approved" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Approve + flag
              </button>
              <button data-testid={`netrev-reject-${r.id.slice(0, 8)}`} disabled={!!busy} onClick={() => review(r.id, "rejected")}
                className="px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] border border-red-500/50 text-red-400 flex items-center gap-1.5 disabled:opacity-40">
                {busy === r.id + "rejected" ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />} Reject
              </button>
            </div>
          ) : (
            <p className="font-mono text-[9px] text-zinc-500 border-t border-white/5 pt-2.5">
              <span className="uppercase tracking-[0.15em]">{r.reviewed_by || "staff"}</span>{r.review_note ? ` — ${r.review_note}` : ""} · {String(r.reviewed_at || "").slice(0, 16).replace("T", " ")}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
